import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";

// This is the marketing/detail page for Compass — distinct from
// src/pages/compass/CompassPage.jsx, which is the actual working tool
// (rendered standalone at /compass, without this site's nav/footer).
// This page follows AmyloidConnectPage's live-product structure and
// links out to that tool via the "Launch" buttons below.
export default function CompassProductPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      title: "Multi-Source Trial Search",
      description:
        "Searches ClinicalTrials.gov and NCI's clinical trials database together, so families aren't left checking multiple sites on their own.",
      color: "#B45309",
    },
    {
      icon: <VolunteerActivismIcon sx={{ fontSize: 40 }} />,
      title: "211 Community Resources",
      description:
        "Local support services — financial assistance, housing, transportation, and more — searched alongside clinical trials, not as a separate step.",
      color: "#92400E",
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: 40 }} />,
      title: "AI Copilot",
      description:
        "An AI assistant on hand to explain any search result in plain language — what a trial's eligibility criteria actually mean for your child.",
      color: "#B45309",
    },
    {
      icon: <FamilyRestroomIcon sx={{ fontSize: 40 }} />,
      title: "Built for Pediatric Cancer Families",
      description:
        "Built specifically around what families navigating a pediatric cancer diagnosis actually need to find, not a generic search tool repurposed for the moment.",
      color: "#92400E",
    },
  ];

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #B45309 0%, #92400E 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Live Product"
                color="success"
                sx={{ fontWeight: 700, mb: 3 }}
              />
              <Typography variant="h2" fontWeight={800} gutterBottom>
                Compass
              </Typography>
              <Typography
                variant="h5"
                sx={{ opacity: 0.9, mb: 4, fontWeight: 400 }}
              >
                A pediatric cancer resource navigator — clinical trials and
                community support, searched together, explained in plain
                language.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LaunchIcon />}
                  onClick={() => navigate("/products/compass")}
                  sx={{
                    bgcolor: "white",
                    color: "#B45309",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  Open Compass
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  borderRadius: 3,
                  p: 3,
                }}
              >
                {[
                  "ClinicalTrials.gov + NCI Search",
                  "211 Community Resource Search",
                  "AI Copilot for Plain-English Guidance",
                  "Built for Pediatric Cancer Families",
                  "Free to Use",
                ].map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: "#FDE68A" }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FEATURES */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Platform Features
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: feature.color, mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #B45309 0%, #92400E 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
            Search clinical trials and community resources together — free,
            and built for families facing a pediatric cancer diagnosis.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<LaunchIcon />}
            onClick={() => navigate("/products/compass")}
            sx={{
              bgcolor: "white",
              color: "#B45309",
              fontWeight: 700,
              px: 5,
              py: 1.5,
              "&:hover": { bgcolor: "#f5f5f5" },
            }}
          >
            Open Compass — It's Free
          </Button>
        </Container>
      </Box>
    </Box>
  );
}